import { useInfiniteQuery } from '@tanstack/react-query';
import { searchMessages } from '../services/conversation.service';

/**
 * Query keys for message search
 *
 * Pattern: ['message-search', conversationId, query]
 */
export const messageSearchKeys = {
  all: ['message-search'] as const,
  search: (conversationId: string, query: string) => [...messageSearchKeys.all, conversationId, query] as const
};

/**
 * Hook for searching messages within a conversation
 *
 * Features:
 * - Infinite scroll pagination (20 items/page)
 * - Auto-cancellation on query changes
 * - Min 3 chars required
 * - 30s stale time for caching
 * - Retry 3x with exponential backoff
 *
 * @param conversationId - Target conversation ID
 * @param query - Search term (min 3 chars)
 * @param enabled - Enable query (should check length >= 3)
 *
 * @example
 * ```typescript
 * const isActive = drawerOpen && query.length >= 3;
 * const { data, fetchNextPage, hasNextPage } = useMessageSearch(
 *   conversationId,
 *   query,
 *   isActive
 * );
 * ```
 */
export const useMessageSearch = (conversationId: string | undefined, query: string, enabled: boolean) => {
  return useInfiniteQuery({
    queryKey: messageSearchKeys.search(conversationId || '', query),
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      searchMessages({
        conversationId: conversationId!,
        query,
        page: pageParam,
        perPage: 20
      }),
    getNextPageParam: (lastPage) => {
      const hasMore = lastPage.page * lastPage.perPage < lastPage.totalCount;
      return hasMore ? lastPage.page + 1 : undefined;
    },
    staleTime: 30000,
    retry: (failureCount) => failureCount < 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    enabled: enabled && !!conversationId && query.length >= 3,
    initialPageParam: 1
  });
};
